import { create } from "zustand";
import type { AuthState, LoginRequest, SignupRequest, LoginResponse, ValidateOtpRequest } from "../types";
import type { User } from "@/modules/user/types";
import { authApi } from "../services/auth-api";

interface ExtendedAuthState extends AuthState {
	// Auth verification state (centralized)
	loading: boolean;
	initialized: boolean;
	isBrandAvailable: boolean | null;
	isNameAvailable: boolean | null;

	// Prevent duplicate requests
	_verifyPromise: Promise<User | null> | null;

	// Actions
	verifyAuth: () => Promise<User | null>;
	resetAuthState: () => void;
	updateAvatar: (file: File) => Promise<User | null>;
	deleteAvatar: () => Promise<User | null>;
}

export const useAuthStore = create<ExtendedAuthState>((set, get) => ({
	isAuthenticated: false,
	currentWorkspaceId: null,
	user: null,

	// Centralized auth verification state
	loading: true,
	initialized: false,
	isBrandAvailable: null,
	isNameAvailable: null,

	// Internal promise tracker to prevent duplicate requests
	_verifyPromise: null,

	login: async (request: LoginRequest): Promise<LoginResponse | null> => {
		const result = await authApi.login(request);
		if (result.success && result.data) {
			return result.data;
		}
		if (result.status === 404) {
			throw new Error("ACCOUNT_NOT_FOUND");
		}
		throw new Error(result.message || "Failed to send verification code");
	},

	signup: async (request: SignupRequest): Promise<LoginResponse | null> => {
		const result = await authApi.signup(request);
		if (result.success && result.data) {
			return result.data;
		}
		if (result.status === 409) {
			if (result.error?.is_invited) {
				throw new Error("ACCOUNT_INVITED");
			}
			throw new Error("ACCOUNT_EXISTS");
		}
		throw new Error(result.message || "Failed to create account");
	},

	logout: async (): Promise<void> => {
		await authApi.logout();
		set({
			isAuthenticated: false,
			user: null,
			currentWorkspaceId: null,
			isNameAvailable: null,
			isBrandAvailable: null,
			loading: false,
			initialized: true,
			_verifyPromise: null,
		});
	},

	validateOtp: async (request: ValidateOtpRequest): Promise<boolean> => {
		const result = await authApi.validateOtp(request);
		console.log("OTP Validation Result:", result);
		if (result.success) {
			// Track registration event
			const { trackEvent } = await import("@/lib/analytics");
			trackEvent("sign_up", { method: "email" }, "CompleteRegistration");

			// Apply pending signup profile (name collected during signup)
			try {
				const pendingProfile = localStorage.getItem("pendingSignupProfile");
				if (pendingProfile) {
					const { first_name, last_name } = JSON.parse(pendingProfile);
					if (first_name && last_name) {
						// Only update if the user doesn't already have a name
						const userInfo = await authApi.info();
						if (userInfo.success && userInfo.data && (!userInfo.data.first_name || !userInfo.data.last_name)) {
							await authApi.updateProfile({ first_name, last_name });
						}
					}
					localStorage.removeItem("pendingSignupProfile");
				}
			} catch (e) {
				console.error("Failed to apply pending signup profile:", e);
				localStorage.removeItem("pendingSignupProfile");
			}

			// Trigger a fresh auth verification after OTP validation
			// This will set isAuthenticated, isNameAvailable, etc. atomically
			await get().verifyAuth();
			return true;
		}
		return false;
	},

	getUserInfo: async (): Promise<User | null> => {
		const result = await authApi.info();
		if (result.success && result.data) {
			const user = result.data;
			set({
				user,
				isAuthenticated: true,
				currentWorkspaceId: user.current_workspace_id,
				isNameAvailable: !!user.first_name && !!user.last_name,
				isBrandAvailable: (user.workspaces_count ?? 0) > 0,
			});
			return user;
		}
		return null;
	},

	updateProfile: async (payload: Partial<User>): Promise<User | null> => {
		const result = await authApi.updateProfile(payload);
		if (result.success && result.data) {
			const user = result.data;
			const currentState = get();
			// Preserve isBrandAvailable if workspaces_count is not returned from the API
			const isBrandAvailable = user.workspaces_count !== undefined ? user.workspaces_count > 0 : currentState.isBrandAvailable;
			set({
				user,
				currentWorkspaceId: user.current_workspace_id,
				isNameAvailable: !!user.first_name && !!user.last_name,
				isBrandAvailable,
			});
			// Re-fetch full user data (includes nested relations like partner)
			get().verifyAuth();
		}

		return result.data;
	},

	updateAvatar: async (file: File): Promise<User | null> => {
		const result = await authApi.updateAvatar(file);
		if (result.success && result.data) {
			const user = result.data;
			const currentState = get();
			const isBrandAvailable = user.workspaces_count !== undefined ? user.workspaces_count > 0 : currentState.isBrandAvailable;
			set({
				user,
				currentWorkspaceId: user.current_workspace_id,
				isNameAvailable: !!user.first_name && !!user.last_name,
				isBrandAvailable,
			});
			return user;
		}
		return null;
	},

	deleteAvatar: async (): Promise<User | null> => {
		const result = await authApi.deleteAvatar();
		if (result.success && result.data) {
			const user = result.data;
			const currentState = get();
			const isBrandAvailable = user.workspaces_count !== undefined ? user.workspaces_count > 0 : currentState.isBrandAvailable;
			set({
				user,
				currentWorkspaceId: user.current_workspace_id,
				isNameAvailable: !!user.first_name && !!user.last_name,
				isBrandAvailable,
			});
			return user;
		}
		return null;
	},

	setWorkspaceId(id: string): void {
		set({ currentWorkspaceId: id });
	},

	// Centralized auth verification - prevents duplicate calls
	verifyAuth: async (): Promise<User | null> => {
		const state = get();

		// If already verifying, return the existing promise
		if (state._verifyPromise) {
			console.log("Auth verification already in progress, reusing existing promise");
			return state._verifyPromise;
		}

		console.log("Starting auth verification...");
		set({ loading: true });

		const verifyPromise = (async () => {
			try {
				const result = await authApi.info();

				if (result.success && result.data) {
					const user = result.data;
					set({
						user,
						isAuthenticated: true,
						currentWorkspaceId: user.current_workspace_id,
						isNameAvailable: !!user.first_name && !!user.last_name,
						isBrandAvailable: (user.workspaces_count ?? 0) > 0,
						loading: false,
						initialized: true,
						_verifyPromise: null,
					});
					// console.log("Auth verification successful:", user);
					return user;
				} else {
					set({
						isAuthenticated: false,
						user: null,
						isNameAvailable: null,
						isBrandAvailable: null,
						loading: false,
						initialized: true,
						_verifyPromise: null,
					});
					// console.log("Auth verification failed - not authenticated");
					return null;
				}
			} catch (error) {
				// console.error("Auth verification error:", error);
				set({
					isAuthenticated: false,
					user: null,
					isNameAvailable: null,
					isBrandAvailable: null,
					loading: false,
					initialized: true,
					_verifyPromise: null,
				});
				return null;
			}
		})();

		set({ _verifyPromise: verifyPromise });
		return verifyPromise;
	},

	resetAuthState: (): void => {
		set({
			isAuthenticated: false,
			user: null,
			currentWorkspaceId: null,
			loading: true,
			initialized: false,
			isBrandAvailable: null,
			isNameAvailable: null,
			_verifyPromise: null,
		});
	},
}));
